import React from "react";
import { Link } from "react-router-dom";
import { API, GST_STATES } from "../../../../common/Constants";
import { getAPIHeaders } from "../../../../common/Utils";
import { all_routes } from "../../../router/all_routes";
import PreLoader from "../../../../core/common/preLoader";
import useFetch from "../../../../hooks/useFetch";
import CustomToast from "../../../../core/common/customToast";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import CopyRightInfo from "../../../../core/common/copyRightInfo";
import CommonTabMenu from "../commonTabMenu";
import BillingSettingsMenu from "./setting-menu";
import CommonSelectV2 from "../../../../core/common/commonSelectV2";

interface TaxSettingsForm {
    gst_enabled: boolean;
    gst_state: string;
    gst_state_code: string;
    gst_number: string;
}

const TaxSettings = () => {
    const routes = all_routes;
    const { isLoading, callFetch } = useFetch();
    const [toastSettings, setToastSettings] = React.useState<{
        show: boolean;
        message: string;
        heading: string;
        type: "ERROR" | "SUCCESS" | "INFO" | "";
    }>({
        show: false,
        message: "",
        heading: "",
        type: "ERROR",
    });

    const [taxSettings, setTaxSettings] = React.useState<TaxSettingsForm>({
        gst_enabled: false,
        gst_state: "",
        gst_state_code: "",
        gst_number: ""
    });

    const onToastClose = () => {
        setToastSettings({
            show: false,
            message: "",
            heading: "",
            type: "",
        });
    };

    const fetchTaxSettings = React.useCallback(() => {
        callFetch(API.SETTINGS_ALL,
            'GET',
            getAPIHeaders(),
            null,
            null
        ).then((res) => {
            // Extract SYSTEM settings
            const systemSettings = res?.tenantSettings?.settingGroups?.find((group: any) => group.category === 'SYSTEM')?.settings || [];
            const gstEnabled = systemSettings.find((setting: any) => setting.settingKey === 'gst_enabled')?.currentValue || 'false';
            const gstState = systemSettings.find((setting: any) => setting.settingKey === 'gst_state')?.currentValue || '';
            const gstStateCode = systemSettings.find((setting: any) => setting.settingKey === 'gst_state_code')?.currentValue || '';
            const gstNumber = systemSettings.find((setting: any) => setting.settingKey === 'gst_number')?.currentValue || '';

            setTaxSettings({
                gst_enabled: gstEnabled === 'true' || gstEnabled === true,
                gst_state: gstState,
                gst_state_code: gstStateCode,
                gst_number: gstNumber
            });
        }).catch((err) => {
            setToastSettings({
                show: true,
                message: "Failed to fetch tax settings.",
                heading: "Error",
                type: "ERROR",
            });
        });
    }, []);

    React.useEffect(() => {
        fetchTaxSettings();
    }, []);

    const handleStateChange = (stateCode: string) => {
        // Find the state label from the state code
        const selectedState = GST_STATES.find(state => state.value === stateCode);
        setTaxSettings(prev => ({
            ...prev,
            gst_state: selectedState?.label || '',
            gst_state_code: selectedState?.value || ''
        }));
    };

    const handleInputChange = (field: keyof TaxSettingsForm, value: string) => {
        setTaxSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleToggleGST = (enabled: boolean) => {
        setTaxSettings(prev => ({
            ...prev,
            gst_enabled: enabled
        }));
    };

    const validateForm = (): boolean => {
        // Only validate if GST is enabled
        if (taxSettings.gst_enabled) {
            // GST Number validation
            if (!taxSettings.gst_number.trim()) {
                setToastSettings({
                    show: true,
                    message: "GST Number is required when GST is enabled.",
                    heading: "Validation Error",
                    type: "ERROR",
                });
                return false;
            }

            // GST Number format validation (15 characters alphanumeric)
            const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
            if (!gstPattern.test(taxSettings.gst_number)) {
                setToastSettings({
                    show: true,
                    message: "Please enter a valid GST Number format (e.g., 27AAAAA0000A1Z5).",
                    heading: "Validation Error",
                    type: "ERROR",
                });
                return false;
            }

            // State validation
            if (!taxSettings.gst_state.trim()) {
                setToastSettings({
                    show: true,
                    message: "State is required when GST is enabled.",
                    heading: "Validation Error",
                    type: "ERROR",
                });
                return false;
            }
        }

        return true;
    };

    const saveTaxSettings = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        const payload = [
            {
                settingKey: "gst_enabled",
                scope: "TENANT",
                category: "SYSTEM",
                type: "BOOLEAN",
                value: taxSettings.gst_enabled
            },
            {
                settingKey: "gst_number",
                scope: "TENANT",
                category: "SYSTEM",
                type: "STRING",
                value: taxSettings.gst_number
            },
            {
                settingKey: "gst_state",
                scope: "TENANT",
                category: "SYSTEM",
                type: "STRING",
                value: taxSettings.gst_state
            },
            {
                settingKey: "gst_state_code",
                scope: "TENANT",
                category: "SYSTEM",
                type: "STRING",
                value: taxSettings.gst_state_code
            }
        ];

        callFetch(API.SETTINGS_UPDATE_ALL,
            'PUT',
            getAPIHeaders(),
            null,
            payload
        ).then((res) => {
            setToastSettings({
                show: true,
                message: "Tax settings saved successfully.",
                heading: "Success",
                type: "SUCCESS",
            });
            fetchTaxSettings();
        }).catch((err) => {
            setToastSettings({
                show: true,
                message: "Failed to save tax settings.",
                heading: "Error",
                type: "ERROR",
            });
        });
    };

    const resetForm = () => {
        fetchTaxSettings();
    };

    return (
        <div>
            <>
                <CustomToast
                    show={toastSettings?.show}
                    message={toastSettings?.message}
                    heading={toastSettings?.heading}
                    type={toastSettings?.type || "INFO"}
                    onClose={onToastClose} />
                <PreLoader showLoader={isLoading} />
                {/* Page Wrapper */}
                <div className="page-wrapper">
                    <div className="content">
                        {/* Breadcrumb */}
                        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                            <div className="my-auto mb-2">
                                <h2 className="mb-1">Application Settings</h2>
                                <nav>
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to={routes.adminDashboard}>
                                                <i className="ti ti-smart-home" />
                                            </Link>
                                        </li>
                                        <li className="breadcrumb-item">Administration</li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            Settings
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                        {/* /Breadcrumb */}
                        <CommonTabMenu activeTab="BillingSettings" />
                        <div className="row">
                            <div className="col-xl-3 theiaStickySidebar">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="d-flex flex-column list-group settings-list">
                                            <BillingSettingsMenu activeTab="TaxSettings" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-xl-9">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="border-bottom mb-3 pb-3">
                                            <div className="d-flex align-items-center">
                                                <span className="avatar avatar-xl p-2 me-2 bg-light flex-shrink-0">
                                                    <i className="ti ti-calculator fs-1 text-primary" />
                                                </span>
                                                <div>
                                                    <h4>TAX Settings</h4>
                                                    <p className="text-muted mb-0">Configure tax settings and GST information for your institution</p>
                                                </div>
                                            </div>
                                        </div>
                                        <form onSubmit={saveTaxSettings}>
                                            {/* GST Toggle Section */}
                                            <div className="border rounded p-4 mb-4 bg-light">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <h5 className="mb-2 text-primary">
                                                            <i className="ti ti-receipt-tax me-2" />
                                                            Enable GST
                                                        </h5>
                                                        <p className="text-muted mb-0">
                                                            Turn on to apply GST on transactions and invoices
                                                        </p>
                                                    </div>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="gstToggle"
                                                            checked={taxSettings.gst_enabled}
                                                            onChange={(e) => handleToggleGST(e.target.checked)}
                                                            style={{ 
                                                                width: '3rem', 
                                                                height: '1.5rem',
                                                                cursor: 'pointer'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                {taxSettings.gst_enabled && (
                                                    <div className="alert alert-info d-flex align-items-start mt-3 mb-0" role="alert">
                                                        <i className="ti ti-alert-triangle me-2 fs-5" />
                                                        <div>
                                                            <strong>Disclaimer:</strong> 18% GST will be applicable on all transactions and invoices when GST is enabled.
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* GST Details Section - Only visible when GST is enabled */}
                                            {taxSettings.gst_enabled && (
                                                <div className="border rounded p-4 mb-4">
                                                    <h5 className="mb-3 text-primary">
                                                        <i className="ti ti-map-pin me-2" />
                                                        GST Information
                                                    </h5>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    GST Number (GSTIN) <span className="text-danger">*</span>
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder="27AAAAA0000A1Z5"
                                                                    value={taxSettings.gst_number}
                                                                    onChange={(e) => handleInputChange('gst_number', e.target.value.toUpperCase())}
                                                                    maxLength={15}
                                                                    required={taxSettings.gst_enabled}
                                                                />
                                                                <small className="form-text text-muted">
                                                                    Enter 15-character GSTIN (e.g., 27AAAAA0000A1Z5)
                                                                </small>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label">
                                                                    State <span className="text-danger">*</span>
                                                                </label>
                                                                <CommonSelectV2
                                                                    options={GST_STATES}
                                                                    value={GST_STATES.find(state => state.value === taxSettings.gst_state_code) || null}
                                                                    onChange={handleStateChange}
                                                                    placeHolder="Select State"
                                                                    className="select"
                                                                />
                                                                <small className="form-text text-muted">
                                                                    Select the state where your institution is registered for GST
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Info Alert */}
                                                    <div className="alert alert-info d-flex align-items-start" role="alert">
                                                        <i className="ti ti-info-circle me-2 fs-5" />
                                                        <div>
                                                            <strong>Important:</strong> Make sure your GST information is accurate. 
                                                            This will be used for generating invoices and tax calculations.
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="d-flex align-items-center justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-light border me-3"
                                                    onClick={resetForm}
                                                >
                                                    Reset
                                                </button>
                                                <button type="submit" className="btn btn-primary">
                                                    Save Tax Settings
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="footer d-sm-flex align-items-center justify-content-center border-top bg-white p-3">
                        <CopyRightInfo />
                    </div>
                </div>
                {/* /Page Wrapper */}
            </>
        </div>
    );
};

export default TaxSettings;

